import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BoxProps, Tip } from 'grommet';
import { Edit, Filter } from 'grommet-icons';
import { LoadingLayout } from './LoadingLayout';
import { Box, Card, CardBody, CardHeader, Input, Select, Text } from '/src/components';
import { FilterItem } from '/src/lib/types';
import { toSnakeCase } from '/src/utils';

export const Filters: React.FC<FiltersProps> = (props) => {
  const {
    isFiltering,
    isLoading,
    onSubmit,
    onClear,
    filters,
    width,
    hasUnapplied = false,
    submitLabel,
    description,
    ...boxProps
  } = props;
  const { t } = useTranslation();

  const [isApplyFocused, setIsApplyFocused] = useState(false);
  const [isClearFocused, setIsClearFocused] = useState(false);

  return (
    <Box width={width || '20%'}>
      <Card flex="shrink" {...boxProps} style={{ position: 'relative' }}>
        {isLoading && <LoadingLayout />}
        <CardHeader title={t('common:filters')} icon={<Filter size="24px" color="brand" />}>
          {hasUnapplied && (
            <Box justify="center">
              <Tip content={t('common:unsavedFilters')}>
                <Edit size="16px" />
              </Tip>
            </Box>
          )}
        </CardHeader>
        <CardBody pad="1.5rem" gap="medium">
          {description && (
            <Text size="small" color="dark-3">
              {description}
            </Text>
          )}
          {filters.map((filter) => (
            <Box key={filter.label} gap="xsmall" {...boxProps}>
              {filter.type === 'input' && (
                <Input
                  id={toSnakeCase(filter.label)}
                  name={filter.label}
                  label={filter.label}
                  setValue={filter.setValue}
                  onSubmit={onSubmit}
                  value={filter.value}
                  fontFamily="Lato, sans-serif"
                  hideOptionalText
                  disabled={filter.disabled}
                />
              )}
              {(!filter.type || filter.type === 'select' || filter.type === 'multi-select') && !!filter.options && (
                <Select
                  id={toSnakeCase(filter.label)}
                  name={filter.label}
                  label={filter.label}
                  placeholder={t('common:choose')}
                  options={filter.options}
                  multiple={filter.type === 'multi-select'}
                  value={filter.value}
                  values={filter.values || []}
                  setValue={filter.setValue}
                  setValues={filter.setValues}
                  onOpen={filter.onOpen}
                  onClose={filter.onClose}
                  onSearch={filter.onSearch}
                  isLoadingOptions={filter.isLoadingOptions}
                  emptySearchMessage={filter.emptySearchMessage}
                  fontFamily="Lato, sans-serif"
                  hideOptionalText
                  disabled={filter.disabled}
                />
              )}
              {filter.type === 'date' && (
                <Input
                  name={filter.label}
                  label={filter.label}
                  fill="horizontal"
                  value={filter.value}
                  setValue={filter.setValue}
                  componentType="date"
                  dateBounds={filter.dateBounds}
                  disabled={filter.disabled}
                />
              )}
            </Box>
          ))}
          <Box direction="row" justify="between" gap="xsmall">
            <Box
              id="apply_filters"
              justify="center"
              align="center"
              fill="horizontal"
              background={isApplyFocused ? 'button-hover' : 'accent-1'}
              elevation={isApplyFocused ? 'small' : undefined}
              pad="0.75rem"
              borderRadius="6px"
              onMouseOver={() => setIsApplyFocused(true)}
              onMouseOut={() => setIsApplyFocused(false)}
              onClick={() => onSubmit()}
            >
              <Text color="white" size="1rem" weight={500}>
                {!isFiltering ? submitLabel || t('common:apply') : t('common:applying')}
              </Text>
            </Box>
            <Box
              id="clear_filters"
              justify="center"
              align="center"
              fill="horizontal"
              pad="0.75rem"
              background={isClearFocused ? 'light-5' : 'light-4'}
              elevation={isClearFocused ? 'small' : undefined}
              border={{ color: 'light-2', size: '0.05rem' }}
              borderRadius="6px"
              onMouseOver={() => setIsClearFocused(true)}
              onMouseOut={() => setIsClearFocused(false)}
              onClick={() => onClear()}
            >
              <Text size="1rem" weight={500}>
                {t('common:clear')}
              </Text>
            </Box>
          </Box>
        </CardBody>
      </Card>
    </Box>
  );
};

export type FiltersProps = BoxProps & {
  isFiltering?: boolean;
  isLoading?: boolean;
  onSubmit: () => void;
  onClear: () => void;
  filters: FilterItem[];
  width?: BoxProps['width'];
  hasUnapplied?: boolean;
  submitLabel?: string;
  description?: string;
};
